import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFirestoreDB } from '../lib/firebase';
import type { DeliveryOrder } from '../types';

/** Firestore uber_pending_orders doc shape (written by webhook). */
interface UberPendingDoc {
  orderId: string;
  storeId?: string;
  eventId?: string;
  receivedAt: number;
  displayId?: string;
  customerName: string;
  items: { name: string; quantity: number; specialInstructions?: string }[];
  estimatedReadyAt?: number | null;
}

function mapPendingToDeliveryOrder(docId: string, data: UberPendingDoc): DeliveryOrder {
  return {
    id: docId,
    platform: 'Uber Eats',
    platformOrderId: data.displayId || data.orderId || docId.slice(0, 8),
    customerName: data.customerName || 'Customer',
    items: data.items || [],
    status: 'new',
    timestamp: data.receivedAt || Date.now(),
    deliveryAddress: '',
    estimatedDeliveryTime: data.estimatedReadyAt || Date.now() + 15 * 60 * 1000,
  };
}

/**
 * Subscribe to Firestore uber_pending_orders and return as DeliveryOrder[] (Uber Eats only).
 * Returns empty array when not enabled (e.g. not signed in).
 */
export function useUberPendingOrders(enabled: boolean): DeliveryOrder[] {
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);

  useEffect(() => {
    if (!enabled) {
      setOrders([]);
      return undefined;
    }
    try {
      const db = getFirestoreDB();
      const coll = collection(db, 'uber_pending_orders');
      const unsub = onSnapshot(
        coll,
        (snapshot) => {
          const list: DeliveryOrder[] = snapshot.docs.map((doc) => {
            const data = doc.data() as UberPendingDoc;
            return mapPendingToDeliveryOrder(doc.id, data);
          });
          list.sort((a, b) => b.timestamp - a.timestamp);
          setOrders(list);
        },
        (err) => {
          console.error('useUberPendingOrders subscription error:', err);
          setOrders([]);
        }
      );
      return () => unsub();
    } catch {
      setOrders([]);
      return undefined;
    }
  }, [enabled]);

  return orders;
}
